import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Titles from "layouts/titles";
import ShowTile from "layouts/titles/showTile/index";
import ShowThreeD from "layouts/titles/ShowThreeD.js";
import PresentationTitles from "prof/presentationTitles";
import PresentationMakerPage from "prof/presentationTitles/PresentationMakerPage";
import UsmlePrepAdminPage from "layouts/usmlePrep";
import {
  filterRoutesByWebPermissions,
  firstAccessibleHomePath,
} from "../utils/filterRoutesByWebPermissions";

function NoModuleAccessPage() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={6} px={3}>
        <MDTypography variant="h5" fontWeight="bold">
          No sections available
        </MDTypography>
        <MDTypography variant="body2" color="text" mt={1}>
          This account does not have access to any dashboard modules yet. Ask an
          admin to grant permissions.
        </MDTypography>
      </MDBox>
    </DashboardLayout>
  );
}

function CustomUserHomeRedirect() {
  const { pathname } = useLocation();
  const permissions = useSelector((state) => state.auth?.web_permissions || {});
  const accessible = filterRoutesByWebPermissions(
    customUserModuleRoutes,
    permissions
  );
  const to = firstAccessibleHomePath(accessible, "/no-access");
  if (pathname === to) return <NoModuleAccessPage />;
  return <Navigate to={to} replace />;
}

/**
 * Routes available to custom_user — filtered by web_permissions in App.js.
 */
const customUserModuleRoutes = [
  {
    name: "threeDTitles",
    key: "threeDTitles",
    route: "/threeDTitles",
    component: <Navigate to="/titles?segment=models" replace />,
  },
  {
    name: "embryologyThreeDTitles",
    key: "embryologyThreeDTitles",
    route: "/embryologyThreeDTitles",
    component: <Navigate to="/embryologyTitles?segment=models" replace />,
  },
  {
    type: "collapse",
    name: "Anatomy",
    key: "anatomyTitles",
    icon: <Icon fontSize="small">accessibility_new</Icon>,
    route: "/anatomyTitles",
    component: <Titles type="anatomy" />,
    webModule: "anatomy",
  },
  {
    type: "collapse",
    name: "USMLE Prep",
    key: "usmle-prep",
    icon: <Icon fontSize="small">medical_services</Icon>,
    route: "/usmle-prep",
    component: <UsmlePrepAdminPage />,
    webModule: "usmle",
  },
  {
    type: "collapse",
    name: "Histology",
    key: "titles",
    icon: <Icon fontSize="small">image</Icon>,
    route: "/titles",
    component: <Titles type="his" />,
    webModules: ["histology", "three_d_models"],
  },
  {
    type: "collapse",
    name: "Embryology",
    key: "embryologyTitles",
    icon: <Icon fontSize="small">image</Icon>,
    route: "/embryologyTitles",
    component: <Titles type="emb" />,
    webModules: ["embryology", "embryology_3d"],
  },
  {
    type: "collapse",
    name: "Radiology",
    key: "radiologyTitles",
    icon: <Icon fontSize="small">image</Icon>,
    route: "/radiologyTitles",
    component: <Titles type="rad" />,
    webModule: "radiology",
  },
  {
    type: "collapse",
    name: "Presentations",
    key: "presentationTitles",
    icon: <Icon fontSize="small">tv</Icon>,
    route: "/presentationTitles",
    component: <PresentationTitles type="presentation" />,
    webModule: "presentations",
  },
  {
    name: "presentationMakerPage",
    key: "presentationMakerPage",
    route: "/presentationMakerPage/:id/:lang",
    component: <PresentationMakerPage />,
    webModule: "presentations",
  },
];

const customUserRoutes = [
  {
    route: "/dashboard",
    key: "dashboardRedirect",
    component: <CustomUserHomeRedirect />,
  },
  {
    route: "/no-access",
    key: "noAccess",
    component: <NoModuleAccessPage />,
  },
  ...customUserModuleRoutes,
  {
    name: "showThreeD",
    key: "showThreeD",
    route: "/showThreeD/:id/:lang",
    component: <ShowThreeD />,
  },
  {
    name: "showTitle",
    key: "showTitle",
    route: "/showTitle/:id/:lang",
    component: <ShowTile />,
  },
];

export default customUserRoutes;
